// Invitation URLs only choose a Tawla room. The game host comes from hall config.
// Keep the aada_* URL parameters for compatibility with existing game links.
export const TAWLA_GAME_ID = "239";
export const TAWLA_ROOM_SUBID = "100";
const TAWLA_ROOM_SUBIDS = new Set(["100", "101", "102"]);
const TAWLA_AGENT_RID = /^[1-9][0-9]{0,19}$/;

export function readTawlaRoomInvite(search) {
  const query = new URLSearchParams(search);
  const roomIds = query.getAll("aada_room_id");
  const subids = query.getAll("aada_subid");
  if (roomIds.length !== 1 || subids.length > 1) return null;
  const roomId = roomIds[0];
  const subid = subids.length ? subids[0] : TAWLA_ROOM_SUBID;
  if (!roomId || !/^[A-Za-z0-9_-]{1,128}$/.test(roomId) || !TAWLA_ROOM_SUBIDS.has(subid)) return null;
  return { roomId, subid };
}

export function isTawlaRegisteredPlayer(userInfo) {
  return Boolean(userInfo && userInfo.token && !userInfo.is_guest && !userInfo.is_demo);
}

export function tawlaInviteHomePath(search) {
  const invite = readTawlaRoomInvite(search);
  if (!invite) return null;
  const incoming = new URLSearchParams(search);
  const query = new URLSearchParams({ aada_room_id: invite.roomId, aada_subid: invite.subid });
  // Keep referral/channel attribution through registration, never login credentials.
  const agentRids = incoming.getAll("aid");
  if (agentRids.length === 1 && TAWLA_AGENT_RID.test(agentRids[0])) {
    query.set("aid", agentRids[0]);
  }
  ["f", "c", "p"].forEach((key) => {
    const value = incoming.get(key);
    if (value && value !== "undefined" && value.length <= 128) query.set(key, value);
  });
  return `/home?${query.toString()}`;
}

export function tawlaGameUrlWithHall(gameUrl, gameId, hallOrigin) {
  if (String(gameId) !== TAWLA_GAME_ID) return gameUrl;
  const url = new URL(gameUrl, hallOrigin);
  url.searchParams.set("game_id", TAWLA_GAME_ID);
  url.searchParams.set("aada_hall_url", new URL("/home", hallOrigin).href);
  return url.href;
}

/** The outer hall route is authoritative when opening a shared room in the game iframe. */
export function tawlaGameUrlWithRoom(gameUrl, gameId, hallOrigin, outerSearch) {
  if (String(gameId) !== TAWLA_GAME_ID) return gameUrl;
  const url = new URL(tawlaGameUrlWithHall(gameUrl, gameId, hallOrigin));
  const query = new URLSearchParams(outerSearch);
  if (query.has("aada_room_id") || query.has("aada_subid")) {
    url.searchParams.delete("aada_room_id");
    url.searchParams.delete("aada_subid");
    const invite = readTawlaRoomInvite(query);
    if (invite) {
      url.searchParams.set("aada_room_id", invite.roomId);
      url.searchParams.set("aada_subid", invite.subid);
    }
  }
  return url.href;
}

export function tawlaRoomGamePath(search, gameBaseUrl, hallOrigin) {
  const invite = readTawlaRoomInvite(search);
  if (!invite) return null;
  const url = new URL(tawlaGameUrlWithHall(gameBaseUrl, TAWLA_GAME_ID, hallOrigin));
  url.searchParams.set("aada_room_id", invite.roomId);
  url.searchParams.set("aada_subid", invite.subid);
  const query = new URLSearchParams({ game_id: TAWLA_GAME_ID, url: url.href,
    aada_room_id: invite.roomId, aada_subid: invite.subid });
  // Preserve attribution on the hall route in case a cached login expires before game authentication.
  const homeQuery = new URLSearchParams(tawlaInviteHomePath(search).split("?")[1]);
  ["aid", "f", "c", "p"].forEach((key) => {
    const value = homeQuery.get(key);
    if (value) query.set(key, value);
  });
  return `/game?${query.toString()}`;
}

export function tawlaRegistrationPath(homePath) {
  const query = new URLSearchParams({ reg: "1", back_to: homePath });
  const channel = new URLSearchParams(homePath.split("?")[1]).get("c");
  if (channel) query.set("c", channel);
  return `/login?${query.toString()}`;
}

export function tawlaAuthReturnPath(backTo) {
  // Only our local hall entry can become an invitation return target.
  if (typeof backTo !== "string" || !backTo.startsWith("/home?")) return null;
  return tawlaInviteHomePath(backTo.slice("/home".length));
}

/** A room invitation supplies its own commission parent, even before persisted attribution updates. */
export function tawlaRegistrationAgentRid(backTo, storedAgentRid) {
  const homePath = tawlaAuthReturnPath(backTo);
  if (!homePath) return storedAgentRid || "";
  return new URLSearchParams(homePath.split("?")[1]).get("aid") || "";
}
